import type { AgentStatus, RunStatus } from '@agents/shared';
import * as Dialog from '@radix-ui/react-dialog';
import * as Menu from '@radix-ui/react-dropdown-menu';
import { Link, type LinkProps } from '@tanstack/react-router';
import {
  createContext,
  useCallback,
  useContext,
  useId,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type FormEvent,
  type ReactNode,
} from 'react';
import { ApiProblem } from '../lib/api';
import { avatarTone, initials } from '../lib/format';
import { Icon, type IconName } from './icons';

/* ------------------------------------------------------------------ Buttons */

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-solid' | 'link';

export function Button({
  variant = 'secondary',
  size,
  icon,
  iconOnly,
  loading,
  children,
  className,
  disabled,
  type = 'button',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: 'sm';
  icon?: IconName;
  /** Renders a square icon button; pass an aria-label. */
  iconOnly?: boolean;
  loading?: boolean;
}) {
  const cls = [
    'btn',
    variant === 'secondary' ? '' : `btn-${variant}`,
    size === 'sm' ? 'btn-sm' : '',
    iconOnly ? 'btn-icon' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button
      type={type}
      className={cls}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className="spinner" aria-hidden="true" /> : icon && <Icon name={icon} />}
      {children}
    </button>
  );
}

export function BackLink({ children, ...props }: LinkProps & { children: ReactNode }) {
  return (
    <Link {...props} className="back-link">
      <Icon name="arrowLeft" />
      {children}
    </Link>
  );
}

/* ------------------------------------------------------------------ Status */

type Tone = 'success' | 'warning' | 'danger' | 'info' | undefined;

export function StatusPill({ tone, live, children }: { tone?: Tone; live?: boolean; children: ReactNode }) {
  return (
    <span className="status" data-tone={tone} data-live={live || undefined}>
      {children}
    </span>
  );
}

const AGENT_STATUS: Record<AgentStatus, { label: string; tone?: Tone }> = {
  draft: { label: 'Borrador' },
  active: { label: 'Activo', tone: 'success' },
  paused: { label: 'Pausado', tone: 'warning' },
};

const RUN_STATUS: Record<RunStatus, { label: string; tone?: Tone }> = {
  queued: { label: 'En cola', tone: 'info' },
  running: { label: 'En curso', tone: 'info' },
  completed: { label: 'Completada', tone: 'success' },
  failed: { label: 'Fallida', tone: 'danger' },
  timed_out: { label: 'Tiempo agotado', tone: 'danger' },
  cancelled: { label: 'Cancelada' },
};

export function AgentStatusPill({ status }: { status: AgentStatus }) {
  const s = AGENT_STATUS[status];
  return <StatusPill tone={s.tone}>{s.label}</StatusPill>;
}

export function RunStatusPill({ status }: { status: RunStatus }) {
  const s = RUN_STATUS[status];
  return (
    <StatusPill tone={s.tone} live={status === 'queued' || status === 'running'}>
      {s.label}
    </StatusPill>
  );
}

export const runStatusLabel = (s: RunStatus) => RUN_STATUS[s].label;

/* ------------------------------------------------------------------ Avatars */

/** Stable colour per agent (Doc. 03/03 §01): the same agent looks the same in every table. */
export function AgentAvatar({ id, name, size }: { id: string; name: string; size?: 'lg' }) {
  return (
    <span
      className="avatar"
      data-size={size}
      style={{ '--av': `var(--c-${avatarTone(id)})` } as CSSProperties}
      aria-hidden="true"
    >
      {initials(name)}
    </span>
  );
}

export function PersonAvatar({ name, square }: { name: string; square?: boolean }) {
  return (
    <span className="avatar" data-neutral data-shape={square ? undefined : 'round'} aria-hidden="true">
      {initials(name)}
    </span>
  );
}

/* ------------------------------------------------------------------ Forms */

export function Field({
  label,
  optional,
  help,
  error,
  foot,
  children,
}: {
  label: string;
  optional?: boolean;
  help?: ReactNode;
  error?: string;
  /** Right-aligned note under the control (e.g. a character count). */
  foot?: ReactNode;
  children: (props: { id: string; 'aria-invalid'?: boolean; 'aria-describedby'?: string }) => ReactNode;
}) {
  const id = useId();
  const describedBy = [help && !error ? `${id}-help` : '', error ? `${id}-error` : '']
    .filter(Boolean)
    .join(' ');
  return (
    <div className="field">
      <label htmlFor={id}>
        {label}
        {optional && <span className="optional"> (opcional)</span>}
      </label>
      {children({
        id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': describedBy || undefined,
      })}
      {error ? (
        <p className="error" id={`${id}-error`}>
          <Icon name="alert" />
          {error}
        </p>
      ) : (
        (help || foot) && (
          <div className="field-foot">
            {help ? (
              <p className="help" id={`${id}-help`}>
                {help}
              </p>
            ) : (
              <span />
            )}
            {foot}
          </div>
        )
      )}
    </div>
  );
}

export function SwitchRow({
  title,
  description,
  checked,
  onChange,
}: {
  title: string;
  description: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const id = useId();
  return (
    <div className="switch-row">
      <div>
        <strong id={`${id}-title`}>{title}</strong>
        <p id={`${id}-desc`}>{description}</p>
      </div>
      <span className="switch">
        <input
          type="checkbox"
          role="switch"
          checked={checked}
          aria-labelledby={`${id}-title`}
          aria-describedby={`${id}-desc`}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span />
      </span>
    </div>
  );
}

export function Disclosure({
  id,
  title,
  meta,
  defaultOpen,
  children,
}: {
  id?: string;
  title: ReactNode;
  meta?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  // Only the first value counts: later prop changes must not close what the user opened.
  const [initialOpen] = useState(defaultOpen);
  return (
    <details className="disclosure" id={id} open={initialOpen || undefined}>
      <summary>
        <Icon name="chevRight" className="chev" />
        {title}
        {meta && <span className="summary-meta">{meta}</span>}
      </summary>
      <div className="disclosure-body">{children}</div>
    </details>
  );
}

/* ------------------------------------------------------------------ Filters */

export function SearchInput({
  value,
  onChange,
  label,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
  placeholder: string;
}) {
  return (
    <div className="input-icon">
      <Icon name="search" />
      <input
        className="input"
        type="search"
        aria-label={label}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: T; label: string; count?: number }[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={o.value === value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
          {o.count !== undefined && <span className="n">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ Layout */

export function PageHeader({
  back,
  lead,
  title,
  description,
  meta,
  actions,
}: {
  /** A <BackLink> for nested pages; replaces the old breadcrumb bar. */
  back?: ReactNode;
  /** Visual anchor before the title (e.g. the agent avatar). */
  lead?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <>
      {back}
      <header className="page-header">
        <div>
          <div className="page-title">
            {lead}
            <h1>{title}</h1>
          </div>
          {description && <p>{description}</p>}
          {meta && <div className="page-meta">{meta}</div>}
        </div>
        {actions && <div className="page-actions">{actions}</div>}
      </header>
    </>
  );
}

export function EmptyState({
  title,
  icon,
  compact,
  flow,
  children,
  action,
}: {
  title: string;
  icon?: IconName;
  compact?: boolean;
  /** Short sequence of next steps, shown as a numbered row. */
  flow?: { title: string; text: string }[];
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <section className="empty" data-compact={compact || undefined}>
      {icon && (
        <div className="empty-icon">
          <Icon name={icon} size="lg" />
        </div>
      )}
      <h2>{title}</h2>
      <p>{children}</p>
      {flow && (
        <ol className="flow">
          {flow.map((s) => (
            <li key={s.title}>
              <strong>{s.title}</strong>
              {s.text}
            </li>
          ))}
        </ol>
      )}
      {action}
    </section>
  );
}

/* ------------------------------------------------------------------ Feedback */

const ALERT_ICON: Record<'info' | 'warning' | 'danger', IconName> = {
  info: 'info',
  warning: 'alert',
  danger: 'xCircle',
};

export function Alert({
  tone = 'info',
  icon,
  title,
  children,
  actions,
  role,
  className,
}: {
  tone?: 'info' | 'warning' | 'danger';
  icon?: IconName;
  title: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  role?: 'alert' | 'status';
  className?: string;
}) {
  return (
    <div className={['alert', className].filter(Boolean).join(' ')} data-tone={tone} role={role}>
      <Icon name={icon ?? ALERT_ICON[tone]} />
      <div className="alert-body">
        <strong>{title}</strong>
        {children}
      </div>
      {actions ? <div className="alert-actions">{actions}</div> : <span />}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const problem = error instanceof ApiProblem ? error.problem : null;
  const retryable = !problem || problem.status === 0 || problem.status >= 500;
  return (
    <Alert
      tone="danger"
      role="alert"
      title={problem?.title ?? 'Algo salió mal'}
      actions={
        onRetry &&
        retryable && (
          <Button size="sm" icon="rotate" onClick={onRetry}>
            Reintentar
          </Button>
        )
      }
    >
      <p>
        {problem?.detail ?? 'No pudimos cargar esta información. Revisa tu conexión e inténtalo de nuevo.'}
      </p>
      {problem?.requestId && <p className="mono">Referencia: {problem.requestId}</p>}
    </Alert>
  );
}

export function SkeletonRows({ rows = 4 }: { rows?: number }) {
  return (
    <div className="stack" aria-busy="true" aria-label="Cargando" style={{ gap: 12 }}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton" style={{ width: `${90 - i * 12}%` }} />
      ))}
    </div>
  );
}

/** Loading state shaped like the table it replaces, so the page does not jump when data arrives. */
export function TableSkeleton({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="table-wrap" aria-busy="true" aria-label="Cargando">
      <table className="table">
        <thead>
          <tr>
            {Array.from({ length: cols }, (_, j) => (
              <th key={j}>
                <div className="skeleton" style={{ width: 60, height: 10 }} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }, (_, i) => (
            <tr key={i}>
              {Array.from({ length: cols }, (_, j) => (
                <td key={j} data-optional={j > 1 || undefined}>
                  {j === 0 ? (
                    <div className="cell-main">
                      <span className="skeleton" data-shape="avatar" />
                      <div style={{ flex: 1 }}>
                        <div className="skeleton" style={{ width: `${70 - i * 6}%`, marginBottom: 6 }} />
                        <div className="skeleton" style={{ width: `${50 - i * 4}%`, height: 10 }} />
                      </div>
                    </div>
                  ) : (
                    <div className="skeleton" style={{ width: `${60 - j * 8}%` }} />
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function CopyButton({ text, label = 'Copiar' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const toast = useToast();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast('No se pudo copiar', { tone: 'danger', detail: 'Selecciona el texto y cópialo manualmente.' });
    }
  };
  return (
    <Button variant="ghost" size="sm" icon={copied ? 'check' : 'copy'} onClick={() => void copy()}>
      {copied ? 'Copiado' : label}
    </Button>
  );
}

/* ------------------------------------------------------------------ Menus */

export interface MenuAction {
  label: string;
  icon?: IconName;
  danger?: boolean;
  onSelect: () => void;
}

/** "⋯" menu for secondary or risky actions, kept out of the page's primary action row. */
export function OverflowMenu({
  label,
  actions,
  size,
}: {
  label: string;
  actions: MenuAction[];
  size?: 'sm';
}) {
  if (!actions.length) return null;
  return (
    <Menu.Root>
      <Menu.Trigger asChild>
        <Button variant={size ? 'ghost' : 'secondary'} size={size} iconOnly icon="more" aria-label={label} />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content className="menu" align="end" sideOffset={6}>
          {actions.map((a) => (
            <Menu.Item
              key={a.label}
              className="menu-item"
              data-danger={a.danger || undefined}
              onSelect={a.onSelect}
            >
              {a.icon && <Icon name={a.icon} />}
              {a.label}
            </Menu.Item>
          ))}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}

/* ------------------------------------------------------------------ Dialogs */

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Volver',
  onConfirm,
  loading,
  tone = 'danger',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void;
  loading?: boolean;
  tone?: 'danger' | 'primary';
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="overlay" />
        <Dialog.Content className="dialog">
          <div className="dialog-head">
            <Dialog.Title asChild>
              <h2>{title}</h2>
            </Dialog.Title>
            <Dialog.Description asChild>
              <p>{description}</p>
            </Dialog.Description>
          </div>
          <div className="dialog-foot">
            <Dialog.Close asChild>
              <Button>{cancelLabel}</Button>
            </Dialog.Close>
            <Button
              variant={tone === 'danger' ? 'danger-solid' : 'primary'}
              onClick={onConfirm}
              loading={loading}
            >
              {confirmLabel}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Dialog that wraps a short form (create member, create space, limits). */
export function FormDialog({
  open,
  onOpenChange,
  title,
  description,
  submitLabel,
  onSubmit,
  loading,
  size,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  submitLabel: string;
  onSubmit: (e: FormEvent) => void;
  loading?: boolean;
  size?: 'lg';
  children: ReactNode;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="overlay" />
        <Dialog.Content className="dialog" data-size={size}>
          <form onSubmit={onSubmit} noValidate>
            <div className="dialog-head">
              <Dialog.Title asChild>
                <h2>{title}</h2>
              </Dialog.Title>
              {description ? (
                <Dialog.Description asChild>
                  <p>{description}</p>
                </Dialog.Description>
              ) : (
                <Dialog.Description className="visually-hidden">{title}</Dialog.Description>
              )}
            </div>
            <div className="dialog-body">{children}</div>
            <div className="dialog-foot">
              <Dialog.Close asChild>
                <Button variant="ghost">Cancelar</Button>
              </Dialog.Close>
              <Button variant="primary" type="submit" loading={loading}>
                {submitLabel}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/* ------------------------------------------------------------------ Toasts */

interface ToastOptions {
  tone?: 'danger';
  detail?: string;
}

interface Toast extends ToastOptions {
  id: number;
  message: string;
}

type PushToast = (message: string, options?: 'danger' | ToastOptions) => void;

const ToastCtx = createContext<PushToast>(() => undefined);

/**
 * Success toasts confirm with the verb of the action and leave on their own; error toasts stay
 * until dismissed so they are not missed (Doc. 03/03 §09).
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const dismiss = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), []);
  const push = useCallback<PushToast>(
    (message, options) => {
      const opts = typeof options === 'string' ? { tone: options } : (options ?? {});
      const id = Date.now() + Math.random();
      setToasts((t) => [...t, { id, message, ...opts }]);
      if (opts.tone !== 'danger') setTimeout(() => dismiss(id), 4000);
    },
    [dismiss],
  );
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toast-region" aria-live="polite">
        {toasts.map((t) => (
          <div
            key={t.id}
            className="toast"
            data-tone={t.tone}
            role={t.tone === 'danger' ? 'alert' : 'status'}
          >
            <Icon name={t.tone === 'danger' ? 'xCircle' : 'checkCircle'} />
            <div>
              <strong>{t.message}</strong>
              {t.detail && <p>{t.detail}</p>}
            </div>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              icon="x"
              aria-label="Cerrar notificación"
              onClick={() => dismiss(t.id)}
            />
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export const useToast = () => useContext(ToastCtx);

/** Human message for a failed mutation. */
export function problemMessage(err: unknown, fallback: string): string {
  return err instanceof ApiProblem ? (err.problem.detail ?? err.problem.title) : fallback;
}
